import {
  Flex,
  FormControl,
  HStack,
  Input,
  FormErrorMessage,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import {
  useCallback,
  ChangeEvent,
  useEffect,
  useState,
  useMemo,
  memo,
} from "react";
import { useSelector } from "react-redux";
import { NodeProps } from "reactflow";
import { FLOW_ACTIONS } from "../../../../../../common/constants/campaign";
import { DelayActionOptions } from "../../../../../../common/types/campaign";
import { ActionNodeArgs } from "../../../../../../common/types/flow";
import DropdownWithSearch from "../../../../../../components/DropdownWithSearch";
import { selectCampaign } from "../../../campaignSlice";
import { WIDGET_OPTIONS_DETAILS } from "../constants";
import { isActionDataSame } from "../helpers";
import WidgetContainer from "../WidgetContainer";

const optionDetails = WIDGET_OPTIONS_DETAILS[FLOW_ACTIONS.DELAY];

const delayUnitOptions = [
  { label: "Days", value: "days" },
  { label: "Hours", value: "hours" },
  { label: "Minutes", value: "minutes" },
];

function DelayWidget({
  data: { action, groupId, isCandidate, props, selectedExit, selectedGoto },
}: NodeProps<ActionNodeArgs>) {
  const [isValid, setIsValid] = useState(true);
  const { saveDraft, setValidity, setActions, readonly } = props;
  const identities = useMemo(() => {
    return {
      actionId: action.action_id,
      groupId,
      branchId: action.branch_id,
    };
  }, [action, groupId]);
  const actionOptions = useMemo(
    () => action.action_options as DelayActionOptions,
    [action]
  );

  const { activeErrorCheck } = useSelector(selectCampaign);

  const setValidityCallback = useCallback(
    (valid: boolean) => {
      setValidity(valid, identities.actionId);
      setIsValid(valid);
    },
    [setValidity, identities.actionId]
  );

  function setOptions(options: DelayActionOptions) {
    setActions(options, identities.actionId, groupId);
    debouncedSave(options, { actionId: identities.actionId, groupId });
  }

  const debouncedSave = useMemo(() => debounce(saveDraft, 2000), [saveDraft]);

  function handleValueChange(event: ChangeEvent<HTMLInputElement>) {
    const validity = !!(Number(event.target.value) && actionOptions.delay_unit);
    const positiveWholeNumberRegex = /^(\s*|\d+)$/;
    // to check if the string is either empty string or contains positive whole numbers.
    if (positiveWholeNumberRegex.test(event.target.value)) {
      setOptions({
        delay_unit: actionOptions.delay_unit,
        delay_value: Number(event.target.value),
      });
    }
    setValidityCallback(validity);
  }

  useEffect(() => {
    const validity = !!(
      actionOptions.delay_unit && Number(actionOptions.delay_value)
    );
    setValidityCallback(validity);
  }, [actionOptions, setValidityCallback]);

  function handleOptionChange(value: string) {
    const validity = !!(value && actionOptions.delay_value);
    setOptions({
      delay_value: actionOptions.delay_value,
      delay_unit: value,
    });
    setValidityCallback(validity);
  }

  return (
    <WidgetContainer
      activeErrorCheck={activeErrorCheck}
      invalidMessage={isValid ? "" : "Fields are invalid"}
      identities={identities}
      title={optionDetails.label}
      icon={optionDetails.icon}
      color={optionDetails.color}
      isCandidate={isCandidate}
      selectedExit={selectedExit}
      selectedGoto={selectedGoto}
      isDisabled={readonly}
    >
      <Flex
        justifyContent="center"
        alignItems="center"
        h="100%"
        w="100%"
        px="5"
        py="8"
      >
        <FormControl isInvalid={activeErrorCheck && !isValid}>
          <HStack>
            <Input
              value={actionOptions.delay_value || ""}
              type="text"
              onChange={handleValueChange}
              name="delay-input"
              isDisabled={readonly}
            />
            <DropdownWithSearch
              options={delayUnitOptions}
              placeholder="Select unit"
              value={delayUnitOptions.find(
                (option) => option.value === actionOptions.delay_unit
              )}
              onChange={(option) => handleOptionChange(option?.value ?? "")}
              isInvalid={activeErrorCheck && !isValid}
              controlStyle={{ width: "200px" }}
              isDisabled={readonly}
              menuPortalTarget={null}
            />
          </HStack>
          <FormErrorMessage>Invalid delay</FormErrorMessage>
        </FormControl>
      </Flex>
    </WidgetContainer>
  );
}

export default memo(DelayWidget, isActionDataSame);
